import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useParams } from "react-router-dom";
import { toaster } from "evergreen-ui";
import { FiChevronDown, FiChevronUp, FiExternalLink, FiLink, FiX } from "react-icons/fi";
import { colors } from "./colors";
import { displayEntity, isUrlEntity } from "./drops";
import { formatDay } from "./format";
import SeverityBadge from "./SeverityBadge";
import CauseTag from "./CauseTag";
import PanelBody from "./PanelBody";
import { OutlineButton } from "./Filters";
import { MONO } from "./Row";

/** The selected drop's diagnosis, over the page from the right. */
export default function Panel({ drop, measure, site, events, hasPrev, hasNext, onClose, onPrev, onNext }) {
    const { workspaceNumber } = useParams();
    const panelRef = useRef(null);
    const isOpen = drop !== null;

    useEffect(() => {
        if (!isOpen) return undefined;
        panelRef.current?.focus();
        const onKey = (event) => { if (event.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    if (!drop) return null;

    // The URL already names the drop (`?drop=`), so the page's own address is the link to it.
    const copyLink = () => navigator.clipboard.writeText(window.location.href)
        .then(() => toaster.success("Link copied", { description: "Share it with your team to review this drop." }))
        .catch(() => toaster.danger("Could not copy the link"));
    const relatedEvent = drop.relatedEventId ? events.find((event) => event.id === drop.relatedEventId) : undefined;

    return (
        <Overlay>
            <Backdrop onClick={onClose} aria-hidden />
            <Aside ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="drop-panel-title">
                <Header>
                    <Between>
                        <Tags>
                            <SeverityBadge severity={drop.severity} />
                            <CauseTag cause={drop.cause} />
                            <Meta>{drop.entityType} · Started {formatDay(drop.detectedAt)}</Meta>
                        </Tags>
                        <Tags>
                            <IconButton type="button" onClick={onPrev} disabled={!hasPrev} aria-label="Previous drop"><FiChevronUp /></IconButton>
                            <IconButton type="button" onClick={onNext} disabled={!hasNext} aria-label="Next drop"><FiChevronDown /></IconButton>
                            <Divider aria-hidden />
                            <IconButton type="button" onClick={onClose} aria-label="Close panel"><FiX /></IconButton>
                        </Tags>
                    </Between>
                    <Title id="drop-panel-title" $url={isUrlEntity(drop)}>{displayEntity(drop)}</Title>
                    <Summary>{drop.summary}</Summary>
                </Header>

                <Scroll>
                    <PanelBody drop={drop} measure={measure} relatedEventTitle={relatedEvent?.title} />
                </Scroll>

                <Footer>
                    <Tags>
                        <OutlineButton type="button" onClick={copyLink}><FiLink aria-hidden />Copy link</OutlineButton>
                        {drop.pageId && (
                            <OutlineButton as={Link} to={`/ws/${workspaceNumber}/issues/${drop.pageId}?since=${drop.detectedAt}`}>Issue deep dive</OutlineButton>
                        )}
                        <DarkLink href={site.searchConsoleUrl} target="_blank" rel="noreferrer">
                            Open in Search Console
                            <FiExternalLink aria-hidden />
                        </DarkLink>
                    </Tags>
                </Footer>
            </Aside>
        </Overlay>
    );
}

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideIn = keyframes`from { opacity: 0; transform: translateX(48px); } to { opacity: 1; transform: none; }`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1300;
    display: flex;
    justify-content: flex-end;
`;

const Backdrop = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(15, 20, 25, 0.25);
    animation: ${fadeIn} 200ms cubic-bezier(0.23, 1, 0.32, 1);
`;

const Aside = styled.aside`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 580px;
    height: 100%;
    outline: none;
    background: ${colors.surface};
    box-shadow: 0 25px 50px -12px rgba(15, 20, 25, 0.25);
    animation: ${slideIn} 240ms cubic-bezier(0.23, 1, 0.32, 1);
    @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Header = styled.header`
    padding: 16px 24px;
    border-bottom: 1px solid ${colors.line};
`;

const Between = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const Tags = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
`;

const Meta = styled.span`
    font-size: 12px;
    color: ${colors.muted};
`;

const IconButton = styled.button`
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 6px;
    background: none;
    font-size: 16px;
    color: ${colors.muted};
    cursor: pointer;
    &:hover { background: ${colors.canvas}; color: ${colors.ink}; }
    &:disabled { pointer-events: none; opacity: 0.4; }
`;

const Divider = styled.span`
    width: 1px;
    height: 20px;
    background: ${colors.line};
`;

const Title = styled.h2`
    margin: 12px 0 0;
    word-break: break-word;
    font-weight: 600;
    color: ${colors.ink};
    font-family: ${({ $url }) => ($url ? MONO : "inherit")};
    font-size: ${({ $url }) => ($url ? 16 : 18)}px;
`;

const Summary = styled.p`
    margin: 4px 0 0;
    font-size: 14px;
    color: ${colors.muted};
`;

const Scroll = styled.div`
    flex: 1;
    overflow-y: auto;
`;

const Footer = styled.footer`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 12px 24px;
    border-top: 1px solid ${colors.line};
    background: ${colors.surface};
`;

const DarkLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    white-space: nowrap;
    border-radius: 8px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 500;
    color: #fff;
    background: ${colors.ink};
    &:hover { color: #fff; text-decoration: none; background: ${colors.ink}d9; }
`;
